import { useCallback, useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import type { GridRowSelectionModel } from '@mui/x-data-grid-premium';

import { getIncludedIds } from '@modules/_shared/utils/gridRowSelection';
import {
  BULK_PUBLISH_TRANSACTIONS,
  BULK_UNPUBLISH_TRANSACTIONS,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';

export const useBulkPublishTransactions = ({
  selectedRows,
  setSelectedRows,
  refetchQueries,
}: {
  selectedRows: GridRowSelectionModel;
  setSelectedRows: (rows: GridRowSelectionModel) => void;
  refetchQueries: InternalRefetchQueriesInclude;
}) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [skippedMessage, setSkippedMessage] = useState<string | null>(null);

  const [bulkPublish, { loading: bulkPublishLoading }] = useMutation(
    BULK_PUBLISH_TRANSACTIONS,
    { onError: setErrorLike },
  );
  const [bulkUnpublish, { loading: bulkUnpublishLoading }] = useMutation(
    BULK_UNPUBLISH_TRANSACTIONS,
    { onError: setErrorLike },
  );
  const bulkUpdateLoading = bulkPublishLoading || bulkUnpublishLoading;

  const handleBulkPublish = useCallback(
    async (isPublished: boolean) => {
      const includedIds = getIncludedIds(selectedRows);
      if (includedIds == null) {
        // Exclude-mode selection ("select all minus N"). We don't have the
        // full row list at this layer; require the caller to switch to
        // include-mode before publishing. See gridRowSelection.ts.
        return;
      }
      if (includedIds.length === 0) {
        return;
      }
      const idsArr = includedIds;
      const ids = idsArr.map(id => String(id));

      const emptySelection: GridRowSelectionModel = {
        type: 'include',
        ids: new Set(),
      };

      if (isPublished) {
        const result = await bulkPublish({
          variables: { ids },
          refetchQueries,
        });
        const returning = result.data?.update_property_transactions?.returning;
        if (!returning) {
          return;
        }
        const publishedIds = new Set(
          returning.map((r: { id: string }) => r.id),
        );
        const skippedIds = idsArr.filter(id => !publishedIds.has(String(id)));
        if (skippedIds.length > 0) {
          setSkippedMessage(
            t(
              'Published {{published}} of {{total}} transactions. {{skipped}} skipped (not approved).',
              {
                published: publishedIds.size,
                total: idsArr.length,
                skipped: skippedIds.length,
              },
            ),
          );
          setSelectedRows({ type: 'include', ids: new Set(skippedIds) });
        } else {
          setSelectedRows(emptySelection);
        }
      } else {
        const result = await bulkUnpublish({
          variables: { ids },
          refetchQueries,
        });
        if (result.data?.update_property_transactions != null) {
          setSelectedRows(emptySelection);
        }
      }
    },
    [
      bulkPublish,
      bulkUnpublish,
      selectedRows,
      setSelectedRows,
      refetchQueries,
      t,
    ],
  );

  return {
    apolloError,
    setErrorLike,
    skippedMessage,
    setSkippedMessage,
    bulkPublishLoading,
    bulkUnpublishLoading,
    bulkUpdateLoading,
    handleBulkPublish,
  };
};
